import { TileMap, TileSize } from "game-common/models"
import { FOREST_DIRT_EDGE_START, FOREST_DIRT_TILE_COUNT } from "game-common/room/ember/forest_dirt_art"
import { GroundReader, RiseMapSource, readAtlasGround } from "./ground_rise"
import { contentTexture } from "./content_textures"

type WaterGroundSource = RiseMapSource & { getWaterLayer?(): TileMap | undefined }

/** Ground transitions beside water are drawn below the water, rather than with map2's lily pads. */
export const waterGroundOverlayAt = (source: WaterGroundSource, x: number, y: number): string | undefined => {
    const water = source.getWaterLayer?.()
    if (!water) return undefined
    const tile = source.resolveTileMapFromLayerId("map2")?.[x]?.[y]
    if (!tile || tile === "0") return undefined
    const id = source.resolveTilesetTileId(tile)
    if (!id.startsWith("beach-pebbles-") && !id.startsWith("beach-sand-")) {
        if (!id.startsWith("forest-dirt-")) return undefined
        const position = Number(id.slice("forest-dirt-".length)) - 1
        if (position < FOREST_DIRT_EDGE_START || position >= FOREST_DIRT_TILE_COUNT || !Number.isInteger(position)) return undefined
    }
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        if (Number(water[x + dx]?.[y + dy]) > 0) return id
    }
    return undefined
}

/** Composite the retained ground blends into the ground sampled by the bank and its reflection. */
export const readWaterGround: GroundReader = (source: WaterGroundSource, px, py, w, h) => {
    const ground = readAtlasGround(source, px, py, w, h)
    if (!ground) return undefined
    const overlays: Array<{ x: number; y: number; id: string }> = []
    for (let y = Math.floor(py / TileSize); y * TileSize < py + h; y++) {
        for (let x = Math.floor(px / TileSize); x * TileSize < px + w; x++) {
            const id = waterGroundOverlayAt(source, x, y)
            if (id) overlays.push({ x, y, id })
        }
    }
    if (!overlays.length) return ground
    const canvas = document.createElement("canvas")
    canvas.width = w; canvas.height = h
    const context = canvas.getContext("2d")
    if (!context) return undefined
    const pixels = context.createImageData(w, h)
    pixels.data.set(ground)
    context.putImageData(pixels, 0, 0)
    for (const { x, y, id } of overlays) {
        const texture = contentTexture(id)
        const image = (texture?.baseTexture?.resource as any)?.source
        if (!image || !texture.baseTexture.valid) return undefined
        const frame = texture.frame
        context.drawImage(image, frame.x, frame.y, frame.width, frame.height,
            x * TileSize - px, y * TileSize - py, TileSize, TileSize)
    }
    try { return context.getImageData(0, 0, w, h).data } catch { return undefined }
}

/** Only the relocated cells: all other ground and scenery keep their normal rendering. */
export const waterGroundPatch = (source: WaterGroundSource, ground: Uint8ClampedArray,
    px: number, py: number, w: number, h: number): Uint8ClampedArray | undefined => {
    let patch: Uint8ClampedArray | undefined
    for (let cy = Math.floor(py / TileSize); cy * TileSize < py + h; cy++) {
        for (let cx = Math.floor(px / TileSize); cx * TileSize < px + w; cx++) {
            if (!waterGroundOverlayAt(source, cx, cy)) continue
            patch ??= new Uint8ClampedArray(ground.length)
            const x0 = Math.max(0, cx * TileSize - px), x1 = Math.min(w, (cx + 1) * TileSize - px)
            for (let y = Math.max(0, cy * TileSize - py); y < Math.min(h, (cy + 1) * TileSize - py); y++) {
                const start = (y * w + x0) * 4, end = (y * w + x1) * 4
                patch.set(ground.subarray(start, end), start)
            }
        }
    }
    return patch
}
